import { useCallback, useEffect, useRef } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { Core, WebViewerInstance } from "@pdftron/webviewer"
import AnnotationService, { hexToRgb } from "services/annotations.service"
import type { TViewerPane } from "services/annotations.service"
import { useCreateFigureHighlight } from "hooks/api/highlights/highlights.hook"
import { insertHighlightIntoCache } from "helpers/quotationCache.helper"
import { useFigureHighlightStore } from "store/figureHighlight.store"
import type { TFigureShapeType } from "store/figureHighlight.store"
import { setToolModeOnAllPanes } from "../core/viewerTools"
import { CLOUDY_LINE_STYLE, DEFAULT_HIGHLIGHT_LINE_STYLE } from "./figureHighlight.constants"
import type { AxiosResponse } from "axios"
import type { TFigureHighlight, TCreateFigureHighlightRequest } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TViewerMode } from "store/viewer.store"

export type FigureHighlightCreateContext = {
	instanceRef: MutableRefObject<WebViewerInstance | null>
	// Leaving editor mode (closing the modal back to preview) disarms the tool — preview has no
	// annotate affordances at all, matching the other canvas modes' own "preview has no selection
	// affordances" effect.
	mode: TViewerMode
	// Bumped whenever split screen re-lays-out the viewer (REQ-399). Listed in the "detect the
	// native create tool finishing a shape" effect's deps so arming the tool BEFORE split screen is
	// opened still ends up bound on the split pane once it gets a real document — see that effect's
	// own comment.
	layoutEpoch: number
	numericQuotationIdRef: MutableRefObject<number | undefined>
	queryClient: QueryClient
	formatError: (error: unknown) => void
	// Tears down every OTHER canvas mode (connect-objects, the five "add a detection" sidebars)
	// before arming the shape tool — every other entry point into a canvas mode (handleAddCabinet,
	// etc.) unconditionally TAKES OVER from whatever else is open rather than silently no-opping,
	// and arming the Shapes tool (or its R/O hotkeys) must match that (#1323 review).
	cancelOtherCanvasModes: () => void
	// Cancels the shape tool when a DIFFERENT canvas mode starts (mirrors the `cancelConnectObjects()`
	// call every "add a detection" start function already makes on the others).
	clearSelectionToolbar: () => void
	// Called once a highlight is successfully persisted and rendered, so the "select highlights" flow
	// (useHighlightSelect) can select it immediately and show the delete toolbar — decision 2:
	// the trash icon is reused for both a freshly-drawn highlight and one picked from a prior session.
	onHighlightCreated?: (highlight: TFigureHighlight, pane: TViewerPane) => void
}

// REQ-400 create: arms Apryse's NATIVE RectangleAnnotation/EllipseAnnotation click-drag-to-place
// tool (NOT useAreaCreate's custom backend-rect draft box the other "add X" modes use — this issue
// explicitly asks for the native annotation tool). On mouseup Apryse's own tool has already added a
// live, fully-interactive draft annotation to the document; this hook converts its geometry to
// backend rect space, POSTs, then swaps it for the SAME inert render pipeline #1378 built
// (AnnotationService.addHighlight) instead of leaving the native draft selectable.
export const useFigureHighlightCreate = (ctx: FigureHighlightCreateContext) => {
	const {
		instanceRef,
		mode,
		layoutEpoch,
		numericQuotationIdRef,
		queryClient,
		formatError,
		cancelOtherCanvasModes,
		clearSelectionToolbar,
		onHighlightCreated,
	} = ctx

	const activeShapeTool = useFigureHighlightStore((state) => state.activeShapeTool)
	const selectedStrokeColor = useFigureHighlightStore((state) => state.selectedStrokeColor)
	const selectedFillColor = useFigureHighlightStore((state) => state.selectedFillColor)
	const selectedOpacity = useFigureHighlightStore((state) => state.selectedOpacity)
	const selectedStrokeWidth = useFigureHighlightStore((state) => state.selectedStrokeWidth)
	const selectedLineStyle = useFigureHighlightStore((state) => state.selectedLineStyle)
	const armShapeToolStore = useFigureHighlightStore((state) => state.armShapeTool)
	const disarmShapeToolStore = useFigureHighlightStore((state) => state.disarmShapeTool)

	const { mutateAsync: createFigureHighlightAsync } = useCreateFigureHighlight()

	// Read inside the tool's event handler, which is bound once per arm and must not go stale while
	// a color swatch (or the opacity/stroke-width/line-style controls) changes mid-session (a change
	// doesn't re-arm the tool).
	const selectedStrokeColorRef = useRef(selectedStrokeColor)
	selectedStrokeColorRef.current = selectedStrokeColor
	const selectedFillColorRef = useRef(selectedFillColor)
	selectedFillColorRef.current = selectedFillColor
	const selectedOpacityRef = useRef(selectedOpacity)
	selectedOpacityRef.current = selectedOpacity
	const selectedStrokeWidthRef = useRef(selectedStrokeWidth)
	selectedStrokeWidthRef.current = selectedStrokeWidth
	const selectedLineStyleRef = useRef(selectedLineStyle)
	selectedLineStyleRef.current = selectedLineStyle

	// Returns whether it actually disarmed anything, so the Escape hotkey chain (onCancelCanvasMode)
	// knows to stop here instead of falling through to "close the right sidebar" — same contract as
	// the other cancelCreateX() functions in that chain.
	const disarmShapeTool = useCallback((): boolean => {
		const wasArmed = useFigureHighlightStore.getState().activeShapeTool !== null
		if (!wasArmed) return false
		disarmShapeToolStore()
		const instance = instanceRef.current
		if (instance) setToolModeOnAllPanes(instance, instance.Core.Tools.ToolNames.EDIT)
		return true
	}, [instanceRef, disarmShapeToolStore])

	const armShapeTool = useCallback(
		(shape: TFigureShapeType) => {
			const instance = instanceRef.current
			if (!instance) return
			// Always takes over — matches every other canvas-mode entry point (handleAddCabinet, etc.),
			// none of which silently no-op when something else is open.
			cancelOtherCanvasModes()
			AnnotationService.deselectAll()
			AnnotationService.deselectHighlight()
			clearSelectionToolbar()
			// The header hides the cloudy row for a shape Apryse can't draw a cloud for, but the STORE
			// keeps the last pick across shape switches — so arming such a shape while "cloudy" is
			// selected would persist a style that then silently renders solid. Reset to the default
			// instead. Rectangle → ellipse is the live case on this SDK build.
			const { selectedLineStyle: liveLineStyle, setSelectedLineStyle } = useFigureHighlightStore.getState()
			if (liveLineStyle === CLOUDY_LINE_STYLE && !AnnotationService.supportsCloudyBorder(shape)) {
				setSelectedLineStyle(DEFAULT_HIGHLIGHT_LINE_STYLE)
			}
			armShapeToolStore(shape)
			const { Tools } = instance.Core
			const toolName = shape === "rectangle" ? Tools.ToolNames.RECTANGLE : Tools.ToolNames.ELLIPSE
			setToolModeOnAllPanes(instance, toolName)
		},
		[instanceRef, cancelOtherCanvasModes, clearSelectionToolbar, armShapeToolStore],
	)

	const handleAnnotationAdded = useCallback(
		async (
			annot: Core.Annotations.Annotation,
			shapeType: TFigureShapeType,
			// The manager/pane of the VIEWER whose tool actually fired this event — never assume
			// primary. Apryse's split-screen mode runs a second, fully independent DocumentViewer with
			// its OWN AnnotationManager (see AGENTS.md "Tools and the split screen"); a draft drawn in
			// the split pane lives in the split manager, and force-deleting it through the primary
			// manager is a silent no-op that orphans a raw, fully-movable annotation there forever
			// (#1323 review).
			annotationManager: Core.AnnotationManager,
			pane: TViewerPane,
		) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) {
				// Bail out (and drop the native draft) if there's no quotation to create against —
				// should not happen in editor mode, but guards a race on close.
				annotationManager.deleteAnnotations([annot], { force: true })
				return
			}

			const { pageNumber, coordinates } = AnnotationService.getBackendRectFromAnnotation(annot)
			const body: TCreateFigureHighlightRequest = {
				shape_type: shapeType,
				x0: coordinates.x0,
				y0: coordinates.y0,
				x1: coordinates.x1,
				y1: coordinates.y1,
				page_number: pageNumber,
				// Outline and fill are independently settable — the backend's `color` and `fill_color`
				// are separate fields (verified via `pnpm run api:show`), matching the two distinct
				// "Color" / "Fill color" pickers in the toolbar. `fill_color: null` is the fill picker's
				// "no fill" swatch (#1386 review) — the backend's own encoding for a transparent fill,
				// so it travels as-is with no special-casing here.
				color: selectedStrokeColorRef.current,
				fill_color: selectedFillColorRef.current,
				opacity: selectedOpacityRef.current,
				stroke_width: selectedStrokeWidthRef.current,
				line_style: selectedLineStyleRef.current,
			}

			try {
				const { data: created } = await createFigureHighlightAsync({ quotationId: qid, body })
				// The native draft's job ends here — replace it with the SAME inert pipeline every
				// persisted highlight renders through (z-order, permissions), rather than leaving Apryse's
				// own fully-interactive annotation on the canvas. addHighlight renders into the PRIMARY
				// manager and also projects a mirror into the split pane (if open) — see
				// AnnotationService.mirrorHighlightIntoSplitPane — independent of which pane the native
				// draft itself has to be cleaned up from.
				annotationManager.deleteAnnotations([annot], { force: true })
				AnnotationService.addHighlight(created)
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) =>
					insertHighlightIntoCache(cached, created),
				)
				onHighlightCreated?.(created, pane)
			} catch (error) {
				annotationManager.deleteAnnotations([annot], { force: true })
				formatError(error)
			}
		},
		[numericQuotationIdRef, createFigureHighlightAsync, queryClient, formatError, onHighlightCreated],
	)

	// Detect the native create tool finishing a shape. Originally bound via the TOOL object's own
	// `.on("annotationAdded", ...)` (the API Apryse's TS types document for
	// GenericAnnotationCreateTool) — but live testing on this WebViewer 11.9.1 build showed
	// `documentViewer.getTool(ToolNames.RECTANGLE/ELLIPSE)` can come back WITHOUT usable `.on`/`.off`
	// methods even on the fully-initialized PRIMARY viewer, not just an unready split pane. A guard
	// around that (`typeof tool.on === "function"`) stops the crash, but then silently never binds
	// at all: no POST ever fires, the native draft is left with Apryse's default transparent fill,
	// and it's never swapped into the inert Pass -1 pipeline (wrong z-order, nothing persists).
	//
	// Fix: don't rely on the tool object's event emitter at all. Every other "an annotation was
	// added" detector in this codebase (see AnnotationService.setupChangeListener /
	// buildAnnotationChangedHandler) goes through the ANNOTATION MANAGER's own "annotationChanged"
	// event instead — a plain addEventListener on an object this codebase already round-trips
	// through reliably, with no dependency on a specific tool class instance being fully hydrated.
	// Apryse fires "annotationChanged" with action "add" for EVERY annotationManager.addAnnotations
	// call, including our OWN (AnnotationService.addHighlight's, and every entity/label render), so
	// the filter below has to positively identify "a fresh, UNTRACKED Rectangle/EllipseAnnotation of
	// the armed shape" rather than just "any annotation showed up":
	//   - `action !== "add"` / `info?.imported` — not a creation, or a programmatic import; skip.
	//   - `instanceof` the Annotations class matching the CURRENTLY armed shape — a stray entity
	//     highlight (also a RectangleAnnotation) or the freehand tool's own shape doesn't match.
	//   - `AnnotationService.isOwnAnnotation(id)` — true for anything this service already tracks,
	//     which crucially includes addHighlight's own "add" (it registers the id in highlightIds
	//     BEFORE calling addAnnotations, so this listener sees it as already-owned and skips it —
	//     otherwise persisting the highlight would trigger this handler again on its own addition).
	//
	// Bound per pane via `viewer.getAnnotationManager()` (REQ-399: each DocumentViewer in split
	// screen owns its own manager). Index 0 is always "primary"; index 1 (when it exists) is
	// "split", skipped until it actually has a document — getDocumentViewers() reports a second,
	// not-yet-real viewer even when split screen has never been opened (see
	// core/viewerPanes.ts's resolveViewerPanes, which skips it for the same reason). layoutEpoch is
	// in the deps so arming the tool BEFORE split screen opens still ends up bound on the split pane
	// once it gets a real document, instead of silently never binding there.
	useEffect(() => {
		const instance = instanceRef.current
		if (!instance || !activeShapeTool) return
		const { Annotations, getDocumentViewers, documentViewer } = instance.Core
		const viewers = getDocumentViewers?.() ?? [documentViewer]
		const ShapeCtor = activeShapeTool === "rectangle" ? Annotations.RectangleAnnotation : Annotations.EllipseAnnotation

		const unbinds: (() => void)[] = []
		viewers.forEach((viewer, index) => {
			if (!viewer) return
			if (index > 0 && !viewer.getDocument?.()) return
			const pane: TViewerPane = index === 0 ? "primary" : "split"
			const annotationManager = viewer.getAnnotationManager()
			if (!annotationManager) return
			const onChanged = (
				annotations: Core.Annotations.Annotation[],
				action: string,
				info?: { imported?: boolean },
			) => {
				if (action !== "add" || info?.imported) return
				for (const annotation of annotations) {
					if (!(annotation instanceof ShapeCtor)) continue
					if (AnnotationService.isOwnAnnotation(annotation.Id)) continue
					void handleAnnotationAdded(annotation, activeShapeTool, annotationManager, pane)
				}
			}
			annotationManager.addEventListener("annotationChanged", onChanged)
			unbinds.push(() => annotationManager.removeEventListener("annotationChanged", onChanged))
		})

		return () => unbinds.forEach((unbind) => unbind())
	}, [instanceRef, activeShapeTool, handleAnnotationAdded, layoutEpoch])

	// Keep every pane's create-tool defaults in sync with the chosen stroke/fill colors, opacity and
	// stroke width (decision 1: these are picked BEFORE drawing, from the toolbar) so the
	// in-progress drag already previews them instead of Apryse's plain default styling. Best-effort:
	// `defaults` is a documented but loosely-typed Apryse surface, so a failure here only costs the
	// live preview — the persisted highlight is styled correctly regardless
	// (buildHighlightAnnotation). Line style has no equivalent live preview: Apryse's create-tool
	// `defaults` has no dash-pattern field, so it only takes effect once the highlight is persisted
	// and rendered through AnnotationService.addHighlight.
	useEffect(() => {
		const instance = instanceRef.current
		if (!instance) return
		try {
			const { getDocumentViewers, documentViewer, Tools, Annotations } = instance.Core
			const viewers = getDocumentViewers?.() ?? [documentViewer]
			const alpha = selectedOpacity / 100
			const [strokeR, strokeG, strokeB] = hexToRgb(selectedStrokeColor)
			const strokeColor = new Annotations.Color(strokeR, strokeG, strokeB, alpha)
			// "No fill" (#1386 review) previews as a fully transparent fill — the same
			// `Annotations.Color(0, 0, 0, 0)` idiom `buildHighlightAnnotation` already uses for a
			// persisted `fill_color: null`, so the in-progress drag matches what gets saved.
			const fillColor =
				selectedFillColor === null
					? new Annotations.Color(0, 0, 0, 0)
					: new Annotations.Color(...hexToRgb(selectedFillColor), alpha)
			viewers.forEach((viewer, index) => {
				if (!viewer) return
				// Same not-yet-a-real-pane skip as the annotationAdded binding effect above — the
				// split viewer getDocumentViewers() reports before split screen has ever been opened.
				if (index > 0 && !viewer.getDocument?.()) return
				for (const toolName of [Tools.ToolNames.RECTANGLE, Tools.ToolNames.ELLIPSE]) {
					const tool = viewer.getTool(toolName) as unknown as Core.Tools.GenericAnnotationCreateTool | undefined
					if (!tool) continue
					tool.defaults = {
						StrokeColor: strokeColor,
						FillColor: fillColor,
						Opacity: 1,
						StrokeThickness: selectedStrokeWidth,
					}
				}
			})
		} catch (error) {
			console.warn("Could not preview the figure-highlight colors on the create tool:", error)
		}
		// `activeShapeTool` is in the deps for correctness, not tidiness (found reviewing #1324): every
		// other dep is stable from mount — `instanceRef` is a ref, and `layoutEpoch` only moves on
		// split screen — so this ran exactly once, at mount, while `instanceRef.current` was still
		// null (the instance is assigned asynchronously inside initWebViewer's `.then`). `defaults`
		// was therefore never written on a fresh session, and the in-progress shape drew in Apryse's
		// plain default styling until something unrelated re-ran the effect. Re-running on each arm
		// also re-asserts the style in case Apryse reset the tool in between.
	}, [instanceRef, activeShapeTool, selectedStrokeColor, selectedFillColor, selectedOpacity, selectedStrokeWidth, layoutEpoch])

	// Preview mode has no annotate affordances at all — leave the tool armed and it would still be
	// armed (and the tool-options bar still showing) the next time the modal opens in editor mode.
	useEffect(() => {
		if (mode !== "editor") disarmShapeTool()
	}, [mode, disarmShapeTool])

	return {
		activeShapeTool,
		armShapeTool,
		disarmShapeTool,
	}
}
